import {
  Stethoscope,
  Calendar,
  Users,
  MessageSquare,
  Shield,
  Clock,
  CheckCircle,
} from 'lucide-react';

const PLANNED_FEATURES = [
  {
    icon: <Users className="w-6 h-6 text-teal-500" />,
    title: 'Hebammen-Verzeichnis',
    description: 'Verwaltung von verifizierten Hebammen mit Profilen, Spezialisierungen und Verfügbarkeit',
    status: 'planned',
  },
  {
    icon: <Calendar className="w-6 h-6 text-blue-500" />,
    title: 'Terminbuchung',
    description: 'Online-Buchungssystem für Erstgespräche und Vorsorge-Termine bei Hebammen',
    status: 'planned',
  },
  {
    icon: <MessageSquare className="w-6 h-6 text-purple-500" />,
    title: 'Sichere Kommunikation',
    description: 'Ende-zu-Ende verschlüsselte Nachrichten zwischen Schwangeren und Hebammen',
    status: 'planned',
  },
  {
    icon: <Shield className="w-6 h-6 text-emerald-500" />,
    title: 'Verifizierung',
    description: 'Überprüfung der Qualifikationen und Zertifikate von Hebammen',
    status: 'planned',
  },
  {
    icon: <Stethoscope className="w-6 h-6 text-rose-500" />,
    title: 'Dokumentation',
    description: 'Digitale Mutterpass-Ergänzung und Geburtsvorbereitung',
    status: 'planned',
  },
];

const ROADMAP = [
  { quarter: 'Q1 2025', milestone: 'Konzeptphase & Partnergespräche' },
  { quarter: 'Q2 2025', milestone: 'MVP Hebammen-Verzeichnis' },
  { quarter: 'Q3 2025', milestone: 'Terminbuchung & Messaging' },
  { quarter: 'Q4 2025', milestone: 'Vollständige Integration' },
];

export function MidwivesPage() {
  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
            <Stethoscope className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Hebammen-Portal</h1>
            <p className="text-slate-600">Coming Soon - Vorbereitung für Hebammen-Features</p>
          </div>
        </div>
      </div>

      {/* Coming Soon Banner */}
      <div className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-8 text-white mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5" />
          <span className="text-sm font-medium opacity-90">In Entwicklung</span>
        </div>
        <h2 className="text-2xl font-bold mb-2">Hebammen-Integration kommt bald!</h2>
        <p className="text-white/80 max-w-2xl">
          Wir arbeiten an einer umfassenden Integration von Hebammen in die ParentPath-App.
          Schwangere werden Hebammen in ihrer Nähe finden, Termine buchen und sicher kommunizieren können.
        </p>
      </div>

      {/* Planned Features */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Geplante Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLANNED_FEATURES.map((feature, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">{feature.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Roadmap */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Roadmap</h2>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" />

            <div className="space-y-6">
              {ROADMAP.map((item, index) => (
                <div key={index} className="relative flex items-start gap-4 pl-14">
                  {/* Timeline dot */}
                  <div className="absolute left-4 w-5 h-5 rounded-full bg-white border-2 border-teal-500 flex items-center justify-center">
                    {index === 0 && <div className="w-2 h-2 rounded-full bg-teal-500" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-teal-600">{item.quarter}</div>
                    <div className="text-slate-700">{item.milestone}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Admin Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Vorbereitungen für Admins</h2>
        <div className="bg-white rounded-lg shadow-sm divide-y divide-slate-200">
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="text-slate-700">Datenbank-Schema für Hebammen erstellt</span>
            </div>
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Erledigt</span>
          </div>
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="text-slate-700">Admin-RLS-Policies vorbereitet</span>
            </div>
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Erledigt</span>
          </div>
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-500" />
              <span className="text-slate-700">Partner-Hebammen für Pilotphase gewinnen</span>
            </div>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">In Planung</span>
          </div>
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-slate-400" />
              <span className="text-slate-500">Verifizierungsprozess definieren</span>
            </div>
            <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full">Ausstehend</span>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="bg-slate-50 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">Du bist Hebamme oder kennst eine?</h3>
            <p className="text-sm text-slate-600 mt-1">
              Wir suchen Partner-Hebammen für die Pilotphase. Kontaktiere uns unter{' '}
              <a href="mailto:hebammen@parentpath.de" className="text-teal-600 hover:underline">
                hebammen@parentpath.de
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
